import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  Menu, X, Store, User, LogOut, LayoutDashboard, ShieldCheck, Mail, Phone,
  Facebook, Instagram, Linkedin, ChevronDown, Tag, Grid3x3, MapPin,
  Handshake, Building2, Rocket, HeartHandshake, Trophy, Briefcase,
  Map, Navigation, CalendarDays, Newspaper, Sparkles, Ticket, Users, Megaphone, Bot, Rss, HandHeart, FileText, Star, SmartphoneNfc,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import AlertsOptinBanner from "@/components/AlertsOptinBanner";
import NotificationBell from "@/components/NotificationBell";
import CookieBanner from "@/components/CookieBanner";
import ExitIntentSuggestion from "@/components/ExitIntentSuggestion";
import InstallAppBanner from "@/components/InstallAppBanner";
import ShareBar from "@/components/ShareBar";
import { FreeOrgBadge } from "@/components/FreeOrgBadge";
import { BrandMark } from "@/components/BrandMark";
import { BrandLogoFull } from "@/components/BrandLogoFull";
import SiteBanner, { SiteFooterNote } from "@/components/SiteBanner";

// Nav simplifiée : 3 méga-menus + 2 liens directs (au lieu de 11 liens)
const DEALS_MENU = [
  { to: "/bons-plans",  label: "Tous les bons plans",      icon: Tag,        hint: "Toutes les offres promotionnelles actives" },
  { to: "/partenaires", label: "Nos commerces partenaires", icon: Handshake,  hint: "Commerçants, artisans et services locaux" },
  { to: "/categories",  label: "Par catégorie",            icon: Grid3x3,    hint: "Restaurants, beauté, loisirs, maison…" },
  { to: "/villes",      label: "Par ville / pôle",         icon: MapPin,     hint: "Chartres, Dreux, Bonneval, Châteaudun…" },
  { to: "/carte",       label: "Carte interactive",        icon: Map,        hint: "Géolocalisation des commerces" },
  { to: "/autour-de-moi", label: "Autour de moi",          icon: Navigation, hint: "Offres à proximité immédiate" },
];

const PROS_MENU = [
  { to: "/pour-les-professionnels",   label: "Pour les professionnels", icon: Store,        hint: "Pourquoi rejoindre PROXIreduc-28" },
  { to: "/devenir-partenaire",        label: "Devenir partenaire",    icon: Handshake,      hint: "0 € pendant 12 mois · places limitées", freemium: true },
  { to: "/recevoir-des-devis",        label: "Recevoir des devis",    icon: FileText,      hint: "Recevez gratuitement des demandes de devis locales", freemium: true },
  { to: "/offre-fondateurs",          label: "Offre Fondateur",       icon: Rocket,         hint: "Offre de lancement · places limitées" },
];

const SPACES_MENU = [
  { to: "/pour-les-associations",     label: "Pour les associations",         icon: Ticket,         hint: "Avantages pour vos adhérents" },
  { to: "/clubs",                     label: "Mon club",                      icon: Trophy,         hint: "Club sportif ou culturel", free: true },
  { to: "/associations",              label: "Mon association",               icon: HeartHandshake, hint: "Référencer mon association", free: true },
  { to: "/pour-les-cse",              label: "Mon CSE / comité d'entreprise", icon: Briefcase,      hint: "Avantages salariés", free: true },
  { to: "/organismes-collectivites",  label: "Mon organisme / collectivité",  icon: Building2,      hint: "Collectivité, amicale…", free: true },
];

const COMMUNITY_MENU = [
  { to: "/sortir",            label: "Agenda des sorties",     icon: CalendarDays, hint: "Événements, marchés et festivités" },
  { to: "/groupes-facebook",  label: "Communautés Facebook",   icon: Facebook,     hint: "Groupes d'entraide et bons plans FB" },
  { to: "/actu",              label: "Actu Chartres et Eure-et-Loir", icon: Rss,  hint: "La revue de presse locale, chaque matin" },
  { to: "/revues-de-presse",  label: "Revues de presse du jour", icon: Newspaper, hint: "L'essentiel de l'actu locale réuni chaque jour" },
  { to: "/demande-de-devis",  label: "Demandez vos devis",     icon: FileText,     hint: "Devis gratuits de pros locaux près de chez vous" },
  { to: "/blog",              label: "Blog local",             icon: Newspaper,    hint: "Articles, guides et actualités du 28" },
  { to: "/fiches-google",     label: "Fiches Google",          icon: Store,        hint: "Annuaire optimisé des commerces" },
];

function DropdownMenu({ label, items, testId, icon: Lead }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const loc = useLocation();
  useEffect(() => { setOpen(false); }, [loc.pathname, loc.search]);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const active = items.some((it) => loc.pathname === it.to || loc.pathname.startsWith(it.to + "/"));
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={testId}
        className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${active ? "text-[#E05638]" : "text-slate-700 hover:text-slate-900 hover:bg-slate-100"}`}
      >
        {Lead && <Lead size={15} className={active ? "text-[#E05638]" : "text-slate-400"} />}
        {label} <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div className="absolute left-0 mt-2 w-96 bg-white border border-slate-200/90 rounded-2xl shadow-xl overflow-hidden z-50 p-2 animate-in fade-in slide-in-from-top-2 duration-150" data-testid={`${testId}-panel`}>
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink key={it.to} to={it.to}
                className={({ isActive }) =>
                  `flex items-start gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors group ${isActive ? "bg-orange-50" : ""}`
                }>
                <span className="mt-0.5 w-9 h-9 rounded-lg bg-orange-50 group-hover:bg-orange-100 grid place-items-center text-[#E05638] shrink-0 transition-colors"><Icon size={16}/></span>
                <span className="min-w-0">
                  <span className="flex items-center flex-wrap gap-1.5 text-sm font-semibold text-slate-900">
                    {it.label}
                    {it.free && <FreeOrgBadge size="sm" label="Gratuit" testId={`nav-free-badge-${it.to}`} />}
                    {it.freemium && <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5" data-testid={`nav-freemium-badge-${it.to}`}>0 € · 12 mois</span>}
                  </span>
                  <span className="block text-xs text-slate-500 truncate">{it.hint}</span>
                </span>
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const [site, setSite] = useState({});
  const [cmsPages, setCmsPages] = useState([]);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { api.get("/site-settings").then((r) => setSite(r.data || {})).catch(() => {}); }, []);
  useEffect(() => { api.get("/public/pages").then((r) => setCmsPages(r.data || [])).catch(() => {}); }, []);

  const trackLoc = useLocation();
  useEffect(() => {
    try {
      let vid = localStorage.getItem("prx_vid");
      if (!vid) {
        vid = (window.crypto?.randomUUID?.() || String(Date.now()) + Math.random().toString(36).slice(2));
        localStorage.setItem("prx_vid", vid);
      }
      const p = new URLSearchParams(window.location.search);
      api.post("/track/visit", {
        path: trackLoc.pathname, referrer: document.referrer || "",
        utm_source: p.get("utm_source") || "", visitor_id: vid,
      }).catch(() => {});
    } catch (e) { /* noop */ }
  }, [trackLoc.pathname]);

  const onLogout = async () => { await logout(); navigate("/"); };

  const isHome = trackLoc.pathname === "/";
  const loggedIn = user && user !== false;
  const aiTarget = loggedIn ? "/mon-assistant-ia" : "/#assistant-ia";
  // Nav complète dès xl (1280px) pour les visiteurs ; repoussée à 2xl pour les comptes connectés (plus d'onglets)
  const navFlexCls = loggedIn ? "hidden 2xl:flex" : "hidden xl:flex";
  const navHideCls = loggedIn ? "2xl:hidden" : "xl:hidden";

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF8F5]">
      <SiteBanner />
      <InstallAppBanner />
      <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/85 backdrop-blur-md">
        <div className={`max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4 ${isHome ? "min-h-16 py-2" : "h-16"}`}>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0 min-w-0">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
            {isHome ? (
              <BrandLogoFull showTagline className="h-12 sm:h-14 md:h-16 w-auto" data-testid="brand-logo-full" />
            ) : (
              <>
                <BrandMark size={38} data-testid="brand-mark" />
                <span className="font-heading text-xl font-extrabold tracking-tight text-slate-900">
                  PROXI<span className="text-[#E05638]">reduc</span>-28
                </span>
              </>
            )}
          </Link>
          </div>

          <nav className={`${navFlexCls} items-center gap-1`}>
            <DropdownMenu label="Bons plans" items={DEALS_MENU} testId="nav-deals-dd" icon={Tag} />
            <DropdownMenu label="Pros" items={PROS_MENU} testId="nav-pros-dd" icon={Store} />
            <NavLink to="/reseau-pro"
                     className={({ isActive }) => `inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${isActive ? "text-[#E05638]" : "text-slate-700 hover:text-slate-900 hover:bg-slate-100"}`}
                     data-testid="nav-reseau-pro"><Users size={15} className="text-slate-400"/>Réseau Pro</NavLink>
            <NavLink to="/avis"
                     className={({ isActive }) => `inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${isActive ? "text-[#E05638]" : "text-slate-700 hover:text-slate-900 hover:bg-slate-100"}`}
                     data-testid="nav-avis"><Star size={15} className="text-slate-400"/>Avis</NavLink>
            <DropdownMenu label="Clubs & Assos" items={SPACES_MENU} testId="nav-spaces-dd" icon={Trophy} />
            <DropdownMenu label="Vie locale" items={COMMUNITY_MENU} testId="nav-community-dd" icon={CalendarDays} />
            {cmsPages.map((p) => (
              <NavLink key={p.slug} to={`/p/${p.slug}`}
                       className={({ isActive }) => `px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${isActive ? "text-[#E05638]" : "text-slate-700 hover:text-slate-900 hover:bg-slate-100"}`}
                       data-testid={`nav-cms-${p.slug}`}>{p.label}</NavLink>
            ))}
          </nav>

          <div className={`${navFlexCls} items-center gap-2 shrink-0`}>
            {user && user !== false ? (
              <>
                {(user.role === "admin" || user.role === "super_admin") && (
                  <Link to="/admin"><Button variant="outline" size="sm" data-testid="header-admin-btn"><ShieldCheck className="w-4 h-4 mr-1"/> Admin</Button></Link>
                )}
                {user.role === "professional" && (
                  <Link to="/pro"><Button variant="outline" size="sm" data-testid="header-pro-btn"><LayoutDashboard className="w-4 h-4 mr-1"/> Espace Pro</Button></Link>
                )}
                {user.role === "organization" && (
                  <Link to="/organisme"><Button variant="outline" size="sm" data-testid="header-org-btn"><Building2 className="w-4 h-4 mr-1"/> Espace organisme</Button></Link>
                )}
                {user.role === "association" && (
                  <Link to="/espace-association"><Button variant="outline" size="sm" data-testid="header-assoc-btn"><HeartHandshake className="w-4 h-4 mr-1"/> Mon association</Button></Link>
                )}
                {user.role === "club" && (
                  <Link to="/espace-club"><Button variant="outline" size="sm" data-testid="header-club-btn"><Trophy className="w-4 h-4 mr-1"/> Mon club</Button></Link>
                )}
                {user.role === "member" && (
                  <Link to="/mon-pass"><Button variant="outline" size="sm" data-testid="header-member-btn">Mon pass</Button></Link>
                )}
                <Link to="/compte"><Button variant="ghost" size="sm" data-testid="header-account-btn"><User className="w-4 h-4 2xl:mr-1"/> <span className="hidden 2xl:inline">{user.first_name || "Compte"}</span></Button></Link>
                <NotificationBell />
                <Button variant="ghost" size="sm" onClick={onLogout} data-testid="header-logout-btn"><LogOut className="w-4 h-4"/></Button>
              </>
            ) : (
              <>
                <Link to="/connexion"><Button variant="ghost" size="sm" data-testid="header-login-btn">Connexion</Button></Link>
              </>
            )}
          </div>

          <button onClick={() => setOpen((v) => !v)} className={`${navHideCls} p-2 rounded-md hover:bg-slate-100`} data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>

        {open && (
          <div className={`${navHideCls} border-t border-slate-200 bg-white max-h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain`}>
            <nav className="px-4 py-3 pb-28 flex flex-col gap-1">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 flex items-center gap-1.5"><Tag size={12}/> Bons plans</div>
              {DEALS_MENU.map((n) => (
                <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className="px-3 py-2 rounded-md text-slate-700 hover:bg-slate-100 text-sm">{n.label}</NavLink>
              ))}
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mt-3 flex items-center gap-1.5"><Store size={12}/> Commerçants / Professionnels</div>
              {PROS_MENU.map((n) => (
                <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className="px-3 py-2 rounded-md text-slate-700 hover:bg-slate-100 text-sm flex items-center flex-wrap gap-1.5">
                  {n.label}{n.free && <FreeOrgBadge size="sm" label="Gratuit" testId={`nav-mobile-free-badge-${n.to}`} />}
                  {n.freemium && <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5" data-testid={`nav-mobile-freemium-badge-${n.to}`}>0 € · 12 mois</span>}
                </NavLink>
              ))}
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mt-3 flex items-center gap-1.5"><Trophy size={12}/> Mon club / asso / CSE</div>
              {SPACES_MENU.map((n) => (
                <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className="px-3 py-2 rounded-md text-slate-700 hover:bg-slate-100 text-sm flex items-center flex-wrap gap-1.5" data-testid={`nav-mobile-space-${n.to}`}>
                  {n.label}{n.free && <FreeOrgBadge size="sm" label="Gratuit" testId={`nav-mobile-space-free-${n.to}`} />}
                </NavLink>
              ))}
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mt-3 flex items-center gap-1.5"><CalendarDays size={12}/> Vie locale & Sorties</div>
              {COMMUNITY_MENU.map((n) => (
                <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className="px-3 py-2 rounded-md text-slate-700 hover:bg-slate-100 text-sm" data-testid={`nav-mobile-${n.to}`}>{n.label}</NavLink>
              ))}
              <div className="h-px bg-slate-200 my-2" />
              <NavLink to="/reseau-pro" onClick={() => setOpen(false)} className="px-3 py-2 rounded-md text-slate-700 hover:bg-slate-100 text-sm flex items-center gap-2" data-testid="nav-mobile-reseau-pro"><Users size={15} className="text-slate-400"/> Réseau Pro</NavLink>
              <NavLink to="/suggestions" onClick={() => setOpen(false)} className="px-3 py-2 rounded-md font-semibold text-[#E05638] hover:bg-orange-50 text-sm" data-testid="nav-mobile-suggestions">💡 Faire une suggestion</NavLink>
              {cmsPages.map((p) => (
                <NavLink key={p.slug} to={`/p/${p.slug}`} onClick={() => setOpen(false)} className="px-3 py-2 rounded-md text-slate-700 hover:bg-slate-100 text-sm" data-testid={`nav-mobile-cms-${p.slug}`}>{p.label}</NavLink>
              ))}
              <div className="h-px bg-slate-200 my-2" />
              {user && user !== false ? (
                <>
                  {(user.role === "admin" || user.role === "super_admin") && (
                    <Link to="/admin" className="px-3 py-2 rounded-md hover:bg-slate-100 text-sm font-semibold text-[#E05638] flex items-center gap-1.5" onClick={() => setOpen(false)} data-testid="mobile-admin-link"><ShieldCheck className="w-4 h-4"/> Espace Admin</Link>
                  )}
                  {user.role === "professional" && (
                    <Link to="/pro" className="px-3 py-2 rounded-md hover:bg-slate-100 text-sm font-semibold flex items-center gap-1.5" onClick={() => setOpen(false)} data-testid="mobile-pro-link"><LayoutDashboard className="w-4 h-4"/> Espace Pro</Link>
                  )}
                  {user.role === "organization" && (
                    <Link to="/organisme" className="px-3 py-2 rounded-md hover:bg-slate-100 text-sm font-semibold flex items-center gap-1.5" onClick={() => setOpen(false)} data-testid="mobile-org-link"><Building2 className="w-4 h-4"/> Espace organisme</Link>
                  )}
                  {user.role === "association" && (
                    <Link to="/espace-association" className="px-3 py-2 rounded-md hover:bg-slate-100 text-sm font-semibold flex items-center gap-1.5" onClick={() => setOpen(false)} data-testid="mobile-assoc-link"><HeartHandshake className="w-4 h-4"/> Mon association</Link>
                  )}
                  {user.role === "club" && (
                    <Link to="/espace-club" className="px-3 py-2 rounded-md hover:bg-slate-100 text-sm font-semibold flex items-center gap-1.5" onClick={() => setOpen(false)} data-testid="mobile-club-link"><Trophy className="w-4 h-4"/> Mon club</Link>
                  )}
                  {user.role === "member" && (
                    <Link to="/mon-pass" className="px-3 py-2 rounded-md hover:bg-slate-100 text-sm font-semibold flex items-center gap-1.5" onClick={() => setOpen(false)} data-testid="mobile-member-link">Mon pass</Link>
                  )}
                  <Link to="/compte" className="px-3 py-2 rounded-md hover:bg-slate-100 text-sm" onClick={() => setOpen(false)}>Mon compte</Link>
                  <button className="text-left px-3 py-2 rounded-md hover:bg-slate-100 text-sm" onClick={() => { setOpen(false); onLogout(); }}>Déconnexion</button>
                </>
              ) : (
                <>
                  <Link to="/connexion" className="px-3 py-2 rounded-md hover:bg-slate-100 text-sm" onClick={() => setOpen(false)}>Connexion</Link>
                </>
              )}
            </nav>
          </div>
        )}
      </header>

      {!isHome && <ShareBar />}

      <AlertsOptinBanner />

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-white/10 bg-[#0F0F11] text-white mt-16" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid md:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="flex items-center gap-2">
              <BrandLogoFull variant="white" showTagline={false} className="h-9 w-auto" data-testid="footer-logo" />
            </div>
            <p className="mt-3 text-slate-400">{site.tagline || "Les bons plans près de chez vous en Eure-et-Loir."}</p>
            <div className="mt-3 space-y-1 text-slate-400 text-xs">
              {site.contact_email && <div className="flex items-center gap-1.5"><Mail size={12}/> <a href={`mailto:${site.contact_email}`} className="hover:text-[#E05638]">{site.contact_email}</a></div>}
              {site.contact_phone && <div className="flex items-center gap-1.5"><Phone size={12}/> {site.contact_phone}</div>}
            </div>
            {(site.facebook || site.instagram || site.linkedin) && (
              <div className="mt-3 flex items-center gap-2">
                {site.facebook && <a href={site.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white"><Facebook size={14}/></a>}
                {site.instagram && <a href={site.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white"><Instagram size={14}/></a>}
                {site.linkedin && <a href={site.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn" className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white"><Linkedin size={14}/></a>}
              </div>
            )}
          </div>
          <div>
            <div className="font-semibold mb-2 text-white">Navigation</div>
            <ul className="space-y-1 text-slate-400">
              <li><Link to="/bons-plans" className="hover:text-[#E05638]">Tous les bons plans</Link></li>
              <li><Link to="/demande-de-devis" className="hover:text-[#E05638]" data-testid="footer-devis">Faire une demande de devis</Link></li>
              <li><Link to="/petites-annonces" className="hover:text-[#E05638]" data-testid="footer-annonces">Petites annonces</Link></li>
              <li><Link to="/blog" className="hover:text-[#E05638]">Blog local</Link></li>
              <li><Link to="/guides" className="hover:text-[#E05638]" data-testid="footer-guides">Guides & conseils</Link></li>
              <li><Link to="/guide-pratique" className="hover:text-[#E05638]" data-testid="footer-guide-pratique">Guide pratique du 28</Link></li>
              <li><Link to="/revues-de-presse" className="hover:text-[#E05638]" data-testid="footer-revues">Revues de presse</Link></li>
              <li><Link to="/categories" className="hover:text-[#E05638]">Catégories</Link></li>
              <li><Link to="/villes" className="hover:text-[#E05638]">Villes</Link></li>
              <li><Link to="/groupes-facebook" className="hover:text-[#E05638]">Communautés Facebook</Link></li>
              <li><Link to="/sortir" className="hover:text-[#E05638]">Sortir en Eure-et-Loir</Link></li>
              <li><Link to="/carte" className="hover:text-[#E05638]">Carte des partenaires</Link></li>
              <li><Link to="/contact" className="hover:text-[#E05638]">Contact</Link></li>
              {cmsPages.map((p) => (
                <li key={p.slug}><Link to={`/p/${p.slug}`} className="hover:text-[#E05638]" data-testid={`footer-cms-${p.slug}`}>{p.label}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="font-semibold mb-2 text-white">Professionnels & Orgs</div>
            <ul className="space-y-1 text-slate-400">
              <li><Link to="/pour-les-professionnels" className="hover:text-[#E05638]">Pour les professionnels</Link></li>
              <li><Link to="/pour-les-associations" className="hover:text-[#E05638]">Pour les associations, clubs & CSE</Link></li>
              <li><Link to="/devenir-partenaire" className="hover:text-[#E05638]">Devenir partenaire <span className="text-[#E05638] font-semibold">· 0 € / 12 mois</span></Link></li>
              <li><Link to="/recevoir-des-devis" className="hover:text-[#E05638]">Recevoir des devis <span className="text-[#E05638] font-semibold">· gratuit</span></Link></li>
              <li><Link to="/offre-fondateurs" className="hover:text-[#E05638]">Offre Partenaire Fondateur <span className="text-[#E05638] font-semibold">· 0 € / 12 mois</span></Link></li>
              <li><Link to="/organismes-collectivites" className="hover:text-[#E05638]">Espace organisme <span className="text-[#E05638] font-semibold">· gratuit</span></Link></li>
              <li><Link to="/pour-les-cse" className="hover:text-[#E05638]">Pour les CSE & RH <span className="text-[#E05638] font-semibold">· gratuit</span></Link></li>
              <li><Link to="/inscription?role=professional" className="hover:text-[#E05638]">Créer un compte pro <span className="text-[#E05638] font-semibold">· 0 € / 12 mois</span></Link></li>
            </ul>
          </div>
          <div>
            <div className="font-semibold mb-2 text-white">Légal</div>
            <ul className="space-y-1 text-slate-400">
              <li><Link to="/a-propos" className="hover:text-[#E05638]">À propos</Link></li>
              <li><Link to="/mentions-legales" className="hover:text-[#E05638]" data-testid="footer-mentions">Mentions légales</Link></li>
              <li><Link to="/cgv" className="hover:text-[#E05638]" data-testid="footer-cgv">CGV partenaires</Link></li>
              <li><Link to="/cgu" className="hover:text-[#E05638]" data-testid="footer-cgu">CGU</Link></li>
              <li><Link to="/confidentialite" className="hover:text-[#E05638]" data-testid="footer-privacy">Confidentialité</Link></li>
              <li><Link to="/cookies" className="hover:text-[#E05638]" data-testid="footer-cookies">Cookies</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-4 text-center text-xs text-slate-400 space-y-1">
          <div data-testid="footer-partner-sites">
            Nos sites partenaires :{" "}
            <a href="https://proxireduc-28.com" target="_blank" rel="noreferrer" className="hover:text-[#E05638] underline underline-offset-2" data-testid="footer-link-proxireduc">proxireduc-28.com</a>
            {" · "}
            <a href="https://debouche-discount-28.com" target="_blank" rel="noreferrer" className="hover:text-[#E05638] underline underline-offset-2" data-testid="footer-link-debouche">debouche-discount-28.com</a>
          </div>
          <div>© 2026 {site.legal_company || "PROXIreduc-28"} — Eure-et-Loir</div>
        </div>
        <SiteFooterNote />
      </footer>

      <CookieBanner />
      <ExitIntentSuggestion />
    </div>
  );
}
